import React from 'react';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import RepeatIcon from '@mui/icons-material/Repeat';
import RepeatOneIcon from '@mui/icons-material/RepeatOne';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import LyricsIcon from '@mui/icons-material/Lyrics';
import { usePlayer } from '../lib/PlayerContext';
import { useQuery } from '@tanstack/react-query';
import { fetchAllData } from '../lib/api';
import { getArtistNames } from '../utils/artistUtils';
import { LyricsOverlay } from './LyricsOverlay';
import { AudioQualityIndicator } from './AudioQualityIndicator';

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function PlayerControls() {
  const player = usePlayer();
  const { data } = useQuery(['appData'], fetchAllData);
  const progressBarRef = React.useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = React.useState(false);
  const [dragProgress, setDragProgress] = React.useState<number | null>(null);
  const [showLyrics, setShowLyrics] = React.useState(false);

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    player.setVolume(newVolume);
  };

  const updateProgressFromMouse = (clientX: number) => {
    if (!progressBarRef.current) return;
    const rect = progressBarRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const newProgress = Math.max(0, Math.min(1, x / rect.width));
    setDragProgress(newProgress);
  };

  const handleProgressMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    setIsDragging(true);
    updateProgressFromMouse(e.clientX);
  };

  const handleProgressMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    updateProgressFromMouse(e.clientX);
  };

  const handleProgressMouseUp = () => {
    if (!isDragging || dragProgress === null) return;
    player.seek(dragProgress);
    setIsDragging(false);
    setDragProgress(null);
  };

  React.useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isDragging) {
        handleProgressMouseUp();
      }
    };

    document.addEventListener('mouseup', handleGlobalMouseUp);
    return () => document.removeEventListener('mouseup', handleGlobalMouseUp);
  }, [isDragging, dragProgress]);

  const getArtistName = (artistId: string) => {
    if (!data) return 'Unknown Artist';
    return getArtistNames(artistId, data.artists);
  };

  const getVoicebankName = (synthId: string) => {
    if (!data) return 'Unknown Voicebank';
    return data.voicebanks.find(v => v.id === synthId)?.name || 'Unknown Voicebank';
  };

  const displayProgress = isDragging ? (dragProgress ?? player.progress) : player.progress;

  return (
    <>
      <div className="h-24 bg-surface-dark/95 backdrop-blur-lg border-t border-surface-border px-6 py-3 z-50 player-elevation">
        {/* Progress bar */}
        <div 
          ref={progressBarRef}
          className="relative w-full h-1 bg-surface-border rounded-full cursor-pointer mb-3 group transition-all duration-200 hover:h-1.5"
          onMouseDown={handleProgressMouseDown}
          onMouseMove={handleProgressMouseMove}
          onMouseUp={handleProgressMouseUp}
        >
          <div 
            className="absolute h-full bg-gradient-to-r from-naia-pink to-soft-violet rounded-full group-hover:from-pink-light group-hover:to-violet-light transition-all duration-200"
            style={{ 
              width: `${displayProgress * 100}%`,
              transition: isDragging ? 'none' : 'width 0.1s linear'
            }}
          />
          <div className="absolute -top-3 left-0 right-0 text-xs flex justify-between px-1 opacity-0 group-hover:opacity-100 transition-opacity text-text-muted">
            <span>{formatTime(displayProgress * player.duration)}</span>
            <span>{formatTime(player.duration)}</span>
          </div>
          <div 
            className="absolute top-1/2 -mt-2 -ml-2 w-4 h-4 rounded-full bg-pearl-white opacity-0 group-hover:opacity-100 transition-opacity elevation-2"
            style={{ 
              left: `${displayProgress * 100}%`,
              display: isDragging || player.progress > 0 ? 'block' : 'none'
            }}
          />
        </div>

        <div className="flex items-center justify-between gap-8">
          {/* Song info */}
          {player.currentSong && (
            <div className="flex items-center min-w-0 flex-1">
              <div className="relative w-14 h-14 group flex-shrink-0">
                <img 
                  src={player.currentSong.coverUrl}
                  alt={player.currentSong.title}
                  className="w-full h-full rounded-lg object-cover ring-1 ring-surface-border group-hover:ring-naia-pink/50 transition-all duration-200"
                />
                <div className="absolute inset-0 bg-surface-dark/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                  <span className="text-text-primary text-xs font-medium">Now Playing</span>
                </div>
              </div>
              <div className="ml-4 min-w-0">
                <h4 className="font-outfit text-text-primary text-base font-medium truncate hover:text-naia-pink transition-colors cursor-pointer">
                  {player.currentSong.title}
                </h4>
                <p className="text-text-secondary text-sm truncate">
                  {getArtistName(player.currentSong.artistId)} • {getVoicebankName(player.currentSong.synthId)}
                </p>
              </div>
            </div>
          )}

          {/* Controls */}
          <div className="flex items-center gap-6 justify-center">
            <button 
              className="p-2 text-text-muted hover:text-text-primary transition-all duration-300 hover:scale-110 elevation-1 hover:elevation-2 rounded-lg clickable"
              onClick={player.shuffleQueue}
              aria-label="Toggle shuffle"
            >
              <ShuffleIcon className={player.isShuffled ? 'text-naia-pink naia-glow' : ''} />
            </button>
            
            <button 
              className="p-2 text-text-primary hover:text-naia-pink transition-all duration-300 hover:scale-110 elevation-1 hover:elevation-2 rounded-lg clickable"
              onClick={player.playPrevious}
              aria-label="Previous track"
              data-prev-button
              data-keyboard-shortcut="Alt+←"
            >
              <SkipPreviousIcon sx={{ fontSize: 28 }} />
            </button>
            
            <button 
              className="p-3 bg-gradient-to-r from-naia-pink to-pink-light rounded-full text-pearl-white hover:from-pink-light hover:to-naia-pink transition-all duration-300 hover:scale-110 elevation-3 hover:elevation-4 naia-glow clickable"
              onClick={player.togglePlayPause}
              aria-label={player.state === 'playing' ? 'Pause' : 'Play'}
              data-play-button
              data-keyboard-shortcut="Space"
            >
              {player.state === 'playing' ? (
                <PauseIcon sx={{ fontSize: 32 }} />
              ) : (
                <PlayArrowIcon sx={{ fontSize: 32 }} />
              )}
            </button>
            
            <button 
              className="p-2 text-text-primary hover:text-naia-pink transition-all duration-300 hover:scale-110 elevation-1 hover:elevation-2 rounded-lg clickable"
              onClick={player.playNext}
              aria-label="Next track"
              data-next-button
              data-keyboard-shortcut="Alt+→"
            >
              <SkipNextIcon sx={{ fontSize: 28 }} />
            </button>
            
            <button 
              className="p-2 text-text-muted hover:text-text-primary transition-all duration-300 hover:scale-110 elevation-1 hover:elevation-2 rounded-lg clickable"
              onClick={player.toggleRepeat}
              aria-label="Toggle repeat"
            >
              {player.repeatMode === 'one' ? (
                <RepeatOneIcon className="text-naia-pink naia-glow" />
              ) : (
                <RepeatIcon className={player.repeatMode !== 'none' ? 'text-naia-pink naia-glow' : ''} />
              )}
            </button>
          </div>

          {/* Audio Quality, Volume and Lyrics */}
          <div className="flex items-center gap-4 flex-1 justify-end">
            {/* Audio Quality Indicator */}
            <AudioQualityIndicator variant="icon" showSettings={true} />
            
            {player.currentSong?.lrcUrl && (
              <button
                onClick={() => setShowLyrics(true)}
                className="p-2 text-text-muted hover:text-text-primary transition-all duration-300 hover:scale-110 elevation-1 hover:elevation-2 rounded-lg clickable"
                aria-label="Show lyrics"
              >
                <LyricsIcon />
              </button>
            )}
            <div className="flex items-center gap-2 min-w-0">
              <VolumeUpIcon className="text-text-muted flex-shrink-0" />
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={player.volume}
                onChange={handleVolumeChange}
                className="w-20 sm:w-24"
                aria-label="Volume control"
              />
              <span className="text-text-muted text-xs w-8 flex-shrink-0 hidden sm:block">
                {Math.round(player.volume * 100)}%
              </span>
            </div>
          </div>
        </div>
      </div>

      {player.currentSong && (
        <LyricsOverlay
          song={player.currentSong}
          currentTime={player.progress * player.duration}
          isVisible={showLyrics}
          onClose={() => setShowLyrics(false)}
        />
      )}
    </>
  );
} 